import type { TFunction } from 'i18next';

import { z } from 'zod';

export const dynamicShape = (customFields: any[] = [], t: TFunction) => {
  const shape = customFields.reduce<Record<string, z.ZodTypeAny>>((acc, f) => {
    const validation = f.validation || {};
    const { minLength, maxLength, pattern } = validation;

    let zt: z.ZodTypeAny;

    // Step 1: Base type
    switch (f.type) {
      case 'text':
      case 'email':
        zt = z.string().trim();
        if (f.type === 'email') {
          zt = (zt as z.ZodString).email({ message: t('VALID_EMAIL') });
        }
        break;

      case 'number':
        zt = z.coerce.number();
        break;

      case 'boolean': // CHECKBOX
        zt = z.boolean();
        break;

      case 'select':
      case 'date':
        zt = z.string();
        break;

      case 'multiSelect': {
        // 1) Build inner array (pure ZodArray so we can use .min/.max)
        let arr = z.array(z.string());

        // Map minLength/maxLength → min/max selections
        if (typeof minLength === 'number') {
          arr = arr.min(minLength, {
            message: `${t(f.label)} ${t('MIN_VAL_ERR').replace('{NO_CHAR}', String(minLength))}`,
          });
        }
        if (typeof maxLength === 'number') {
          arr = arr.max(maxLength, {
            message: `${t(f.label)} ${t('MAX_VAL_ERR').replace('{NO_CHAR}', String(maxLength))}`,
          });
        }

        // If required and no explicit minLength, require ≥1 selection
        if (f.isRequired && typeof minLength !== 'number') {
          arr = arr.min(1, { message: `${t(f.label)} ${t('IS_REQUIRED')}` });
        }

        // 2) Wrap with preprocess to coerce input shapes into arrays
        const pre = z.preprocess((v) => {
          if (Array.isArray(v)) return v;
          if (v == null || v === '') return []; // null/undefined/'' → []
          return [v]; // single value → [value]
        }, arr);

        // 3) IMPORTANT: don't reassign `pre` (a ZodEffects) to a different generic type.
        // Use a new variable of type ZodTypeAny for optional/default handling.
        let out: z.ZodTypeAny = pre;

        // Optional multiSelects: default to []
        if (!f.isRequired) {
          out = pre.default([]); // leave it NOT optional; undefined → [] via preprocess+default
        }

        zt = out;
        break;
      }

      case 'multiAssignSelect': {
        // the UI stores an array of assignee objects
        const itemSchema = z.object({
          id: z.string().min(1),
          assignedType: z.enum(['USER', 'TEAM']).optional(), // keep optional if backend may omit
          name: z.string().optional(),
          email: z.string().optional(),
        });

        let arr = z.array(itemSchema);

        // If required, require at least 1 selection
        if (f.isRequired) {
          arr = arr.min(1, { message: `${t(f.label)} ${t('IS_REQUIRED')}` });
        }

        // Normalize shapes into arrays
        const pre = z.preprocess((v) => {
          if (Array.isArray(v)) return v;
          if (v == null || v === '') return [];
          return [v];
        }, arr);

        // Optional fields: default to []
        let out: z.ZodTypeAny = pre;
        if (!f.isRequired) {
          out = pre.default([]);
        }

        zt = out;
        break;
      }

      default:
        zt = z.any();
    }

    // Step 2: Apply validation rules for text/number/email
    if ((f.type === 'text' || f.type === 'email') && zt instanceof z.ZodString) {
      if (typeof minLength === 'number') {
        zt = (zt as z.ZodString).min(minLength, {
          message: `${t(f.label)} ${t('MIN_VAL_ERR').replace('{NO_CHAR}', String(minLength))}`,
        });
      }

      if (typeof maxLength === 'number') {
        zt = (zt as z.ZodString).max(maxLength, {
          message: `${t(f.label)} ${t('MAX_VAL_ERR').replace('{NO_CHAR}', String(maxLength))}`,
        });
      }

      if (pattern) {
        try {
          const regexMatch = pattern.match(/^\/(.*)\/([gimsuy]*)$/);
          const regex = regexMatch ? new RegExp(regexMatch[1], regexMatch[2]) : new RegExp(pattern);
          zt = (zt as z.ZodString).regex(regex, { message: t('PATTERN_MSG', { name: f.label }) });
        } catch {
          console.warn(`Invalid regex for field "${f.name}":`, pattern);
        }
      }
    }

    if (f.type === 'number' && zt instanceof z.ZodNumber) {
      if (typeof minLength === 'number') {
        zt = (zt as z.ZodNumber).min(minLength, {
          message: `${f.label} ${t('MIN_VALUE')} ${minLength}`,
        });
      }
      if (typeof maxLength === 'number') {
        zt = (zt as z.ZodNumber).max(maxLength, {
          message: `${f.label} ${t('MAX_VALUE')} ${maxLength}`,
        });
      }
    }

    // Step 3: Required / Optional handling for other types
    if (f.type !== 'multiSelect') {
      if (f.isRequired) {
        if (f.type === 'text' || f.type === 'email' || f.type === 'select' || f.type === 'date') {
          zt = zt.refine((val) => val !== '', {
            message: `${t(f.label)} ${t('IS_REQUIRED')}`,
          });
        } else if (f.type === 'number') {
          zt = zt.refine((val) => val !== undefined && val !== null && !Number.isNaN(val), {
            message: `${t(f.label)} ${t('IS_REQUIRED')}`,
          });
        } else if (f.type === 'boolean') {
          zt = z.boolean().refine((v) => v === true, {
            message: `${t(f.label)} ${t('IS_REQUIRED')}`,
          });
        }
      } else {
        zt = zt.optional();
      }
    }

    acc[f.name] = zt;
    return acc;
  }, {});

  return z.object(shape);
};
